// src/components/housekeeping/StaffManagement.js
import React, { useState } from 'react';
import { supabase } from '../../config/supabase.config';

function StaffManagement({ staff, onUpdate }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    salary: '',
    join_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation 1: Name is required
    if (!formData.name || formData.name.trim().length < 2) {
      alert('Please enter a valid staff name (minimum 2 characters)');
      return;
    }

    // Validation 2: Phone number validation
    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/[- ]/g, ''))) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    // Validation 3: Salary validation
    if (formData.salary) {
      const salary = parseFloat(formData.salary);
      if (isNaN(salary) || salary < 0) {
        alert('Please enter a valid salary amount');
        return;
      }
      if (salary > 1000000) {
        if (!window.confirm('Salary amount seems unusually high. Are you sure?')) {
          return;
        }
      }
    }

    // Validation 4: Join date cannot be in future
    if (formData.join_date > new Date().toISOString().split('T')[0]) {
      alert('Join date cannot be in the future');
      return;
    }

    setLoading(true);
    try {
      const staffData = {
        name: formData.name.trim(),
        phone: formData.phone?.replace(/[- ]/g, '') || null,
        address: formData.address?.trim() || null,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        join_date: formData.join_date,
        is_active: true,
        updated_at: new Date().toISOString()
      };

      if (editingStaff) {
        const { error } = await supabase
          .from('housekeeping_staff')
          .update(staffData)
          .eq('id', editingStaff.id);

        if (error) throw error;
        alert('Staff updated successfully!');
      } else {
        staffData.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('housekeeping_staff')
          .insert([staffData]);

        if (error) throw error;
        alert('Staff added successfully!');
      }

      resetForm();
      onUpdate();
    } catch (error) {
      console.error('Error saving staff:', error);
      if (error.code === '23505') {
        alert('A staff member with this phone number already exists');
      } else {
        alert(`Failed to save staff member: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (member) => {
    // Check for existing attendance/task records
    try {
      // Check attendance records
      const { data: attendanceData } = await supabase
        .from('housekeeping_attendance')
        .select('id')
        .eq('staff_id', member.id)
        .limit(1);

      // Check task logs
      const { data: taskData } = await supabase
        .from('housekeeping_task_log')
        .select('id')
        .eq('staff_id', member.id)
        .limit(1);

      let warningMessage = `Are you sure you want to remove ${member.name}?`;
      
      if (attendanceData?.length > 0 || taskData?.length > 0) {
        warningMessage += '\n\n⚠️ Warning: This staff member has existing records:';
        if (attendanceData?.length > 0) warningMessage += '\n- Attendance records';
        if (taskData?.length > 0) warningMessage += '\n- Task assignments';
        warningMessage += '\n\nThese records will be preserved but the staff will be marked as inactive.';
      }

      if (!window.confirm(warningMessage)) return;

      setLoading(true);
      const { error } = await supabase
        .from('housekeeping_staff')
        .update({ 
          is_active: false
        })
        .eq('id', member.id);

      if (error) throw error;
      
      alert('Staff member removed successfully');
      onUpdate();
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert(`Failed to remove staff member: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      name: member.name,
      phone: member.phone || '',
      address: member.address || '',
      salary: member.salary || '',
      join_date: member.join_date || new Date().toISOString().split('T')[0]
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      address: '',
      salary: '',
      join_date: new Date().toISOString().split('T')[0]
    });
    setEditingStaff(null);
    setShowAddForm(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Staff Management</h3>
        <button
          onClick={() => setShowAddForm(true)}
          disabled={loading}
          className="btn-primary"
          style={{
            padding: '10px 20px',
            background: loading ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          + Add Staff
        </button>
      </div>

      {showAddForm && (
        <div className="housekeeping-form" style={{
          background: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4>{editingStaff ? 'Edit Staff' : 'Add New Staff'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px'
                  }}
                  required
                  minLength="2"
                  maxLength="100"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px'
                  }}
                  placeholder="10 digit number"
                  pattern="[0-9]{10}"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Monthly Salary (₹)
                </label>
                <input
                  type="number"
                  value={formData.salary}
                  onChange={(e) => setFormData({...formData, salary: e.target.value})}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px'
                  }}
                  min="0"
                  max="1000000"
                  step="100"
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Join Date
                </label>
                <input
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData({...formData, join_date: e.target.value})}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px'
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="form-textarea"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '5px'
                }}
                rows="2"
                maxLength="500"
                disabled={loading}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                style={{
                  padding: '10px 20px',
                  background: loading ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : (editingStaff ? 'Update' : 'Add') + ' Staff'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="btn-secondary"
                style={{
                  padding: '10px 20px',
                  background: loading ? '#ccc' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="housekeeping-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Phone</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Address</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Salary</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Join Date</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(member => (
              <tr key={member.id}>
                <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>{member.name}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>{member.phone || '-'}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>{member.address || '-'}</td>
                <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                  {member.salary ? `₹${member.salary.toLocaleString()}` : '-'}
                </td>
                <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                  {member.join_date ? new Date(member.join_date).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                  <button
                    onClick={() => handleEdit(member)}
                    disabled={loading}
                    className="action-btn"
                    style={{
                      marginRight: '5px',
                      padding: '5px 10px',
                      background: loading ? '#ccc' : '#ffc107',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(member)}
                    disabled={loading}
                    className="action-btn"
                    style={{
                      padding: '5px 10px',
                      background: loading ? '#ccc' : '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {staff.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No staff members added yet. Click "Add Staff" to get started.
        </div>
      )}

      {loading && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          zIndex: 9999
        }}>
          Processing...
        </div>
      )}
    </div>
  );
}

export default StaffManagement;